// Pannable, zoomable canvas of cards with edges, from goal-tracker: same camera, pointer
// and pinch handling, spring-eased motion. Layout is goal-tracker's sector layout over the
// subject tree (spec §2.5): children fan outward inside their parent's angular sector.
export type Node = { id: string; depth: number; w: number; h: number; x: number; y: number; tx: number; ty: number; fixed?: boolean; parent?: Node; children: Node[]; rowOffset?: number };
export type EdgeRef = { a: Node; b: Node; el: SVGPathElement };

const HDR = 64;

export class Canvas {
  readonly cam = { x: 0, y: 0, k: 1, tx: 0, ty: 0, tk: 1 };
  nodes: Node[] = [];
  edges: EdgeRef[] = [];
  private raf: number | null = null;
  private drag: { type: "pan"; sx: number; sy: number; ox: number; oy: number; moved: boolean } | { type: "node"; n: Node; el: HTMLElement; sx: number; sy: number; ox: number; oy: number; moved: boolean } | null = null;
  private pinch: { d0: number; mx: number; my: number; k0: number; x0: number; y0: number } | null = null;
  private pts = new Map<number, { x: number; y: number }>();
  onNodeClick: (id: string, e: PointerEvent) => void = () => {};
  onBackgroundClick: () => void = () => {};
  onNodeMoved: (n: Node) => void = () => {};
  onZoom: (k: number) => void = () => {};

  constructor(
    readonly view: HTMLElement,
    readonly world: HTMLElement,
    readonly edgesEl: SVGSVGElement,
    readonly els: Map<string, HTMLElement>,
  ) {
    view.addEventListener("pointerdown", (e) => this.down(e));
    view.addEventListener("pointermove", (e) => this.move(e));
    view.addEventListener("pointerup", (e) => this.up(e));
    view.addEventListener("pointercancel", (e) => this.cancel(e));
    view.addEventListener("wheel", (e) => this.wheel(e), { passive: false });
    addEventListener("resize", () => this.kick());
  }

  narrow() {
    return innerWidth < 700;
  }
  panelWidth() {
    return !this.narrow() && document.body.classList.contains("panel-open") ? 440 : 0;
  }

  kick() {
    if (!this.raf) this.raf = requestAnimationFrame(() => this.tick());
  }
  private tick() {
    this.raf = null;
    let active = false;
    for (const n of this.nodes) {
      const dx = n.tx - n.x, dy = n.ty - n.y;
      if (Math.abs(dx) + Math.abs(dy) > 0.3) {
        n.x += dx * 0.16;
        n.y += dy * 0.16;
        active = true;
      } else {
        n.x = n.tx;
        n.y = n.ty;
      }
      this.position(n);
    }
    const c = this.cam;
    const ddx = c.tx - c.x, ddy = c.ty - c.y, ddk = c.tk - c.k;
    if (Math.abs(ddx) + Math.abs(ddy) > 0.3 || Math.abs(ddk) > 0.001) {
      c.x += ddx * 0.16;
      c.y += ddy * 0.16;
      c.k += ddk * 0.16;
      active = true;
    } else {
      c.x = c.tx;
      c.y = c.ty;
      c.k = c.tk;
    }
    this.applyCam();
    this.drawEdges();
    if (active) this.raf = requestAnimationFrame(() => this.tick());
  }
  private position(n: Node) {
    const el = this.els.get(n.id);
    if (el) {
      el.style.left = n.x + "px";
      el.style.top = n.y + "px";
    }
  }
  drawEdges() {
    for (const e of this.edges) {
      const { a, b } = e;
      const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2, dx = b.x - a.x, dy = b.y - a.y, L = Math.hypot(dx, dy) || 1;
      const cx = mx - (dy / L) * L * 0.1, cy = my + (dx / L) * L * 0.1;
      e.el.setAttribute("d", `M${a.x},${a.y} Q${cx},${cy} ${b.x},${b.y}`);
    }
  }

  applyCam() {
    this.world.style.transform = `translate(${this.cam.x}px,${this.cam.y}px) scale(${this.cam.k})`;
    this.onZoom(this.cam.k);
  }
  camTo(t: { x: number; y: number; k: number }, instant = false) {
    const c = this.cam;
    c.tx = t.x;
    c.ty = t.y;
    c.tk = t.k;
    if (instant) {
      c.x = t.x;
      c.y = t.y;
      c.k = t.k;
      this.applyCam();
    }
    this.kick();
  }
  fitAll(instant = false) {
    const vis = this.nodes.filter((n) => n.depth <= 1);
    if (!vis.length) return;
    let x0 = 1e9, y0 = 1e9, x1 = -1e9, y1 = -1e9;
    for (const n of vis) {
      x0 = Math.min(x0, n.tx - n.w / 2);
      x1 = Math.max(x1, n.tx + n.w / 2);
      y0 = Math.min(y0, n.ty - n.h / 2);
      y1 = Math.max(y1, n.ty + n.h / 2);
    }
    const pad = 60, vw = innerWidth - this.panelWidth(), vh = innerHeight - HDR;
    const k = Math.max(0.12, Math.min(1, (vw - pad * 2) / (x1 - x0), (vh - pad * 2) / (y1 - y0)));
    this.camTo({ k, x: (vw - (x0 + x1) * k) / 2, y: HDR + (vh - (y0 + y1) * k) / 2 }, instant);
  }
  centerNode(n: Node, k = this.cam.tk) {
    const vw = innerWidth - this.panelWidth();
    const vh = this.narrow() ? innerHeight * 0.45 : innerHeight - HDR;
    this.camTo({ k, x: vw / 2 - n.tx * k, y: HDR + vh / 2 - n.ty * k });
  }
  zoomAt(sx: number, sy: number, f: number) {
    const c = this.cam;
    const k = Math.max(0.12, Math.min(2.5, c.tk * f));
    f = k / c.tk;
    c.tx = sx - (sx - c.tx) * f;
    c.ty = sy - (sy - c.ty) * f;
    c.tk = k;
    this.kick();
  }

  private capture(id: number) {
    try {
      this.view.setPointerCapture(id);
    } catch {}
  }
  private down(e: PointerEvent) {
    if (e.button !== 0) return;
    this.pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (this.pts.size === 2) {
      if (this.drag?.type === "node") this.drag.el.classList.remove("dragging");
      this.drag = null;
      this.view.classList.remove("panning");
      for (const id of this.pts.keys()) this.capture(id);
      const [a, b] = [...this.pts.values()] as [{ x: number; y: number }, { x: number; y: number }];
      this.pinch = { d0: Math.hypot(a.x - b.x, a.y - b.y) || 1, mx: (a.x + b.x) / 2, my: (a.y + b.y) / 2, k0: this.cam.k, x0: this.cam.x, y0: this.cam.y };
      return;
    }
    if (this.pinch) return;
    const target = e.target as HTMLElement;
    const nodeEl = target.closest<HTMLElement>(".node");
    if (nodeEl) {
      if (target.closest("button,a,input,label,select")) return;
      const n = this.nodes.find((x) => x.id === nodeEl.dataset.id);
      if (!n) return;
      this.drag = { type: "node", n, el: nodeEl, sx: e.clientX, sy: e.clientY, ox: n.tx, oy: n.ty, moved: false };
    } else this.drag = { type: "pan", sx: e.clientX, sy: e.clientY, ox: this.cam.tx, oy: this.cam.ty, moved: false };
    this.capture(e.pointerId);
  }
  private move(e: PointerEvent) {
    if (this.pts.has(e.pointerId)) this.pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (this.pinch) {
      if (this.pts.size < 2) return;
      const [a, b] = [...this.pts.values()] as [{ x: number; y: number }, { x: number; y: number }];
      const d = Math.hypot(a.x - b.x, a.y - b.y), mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;
      const p = this.pinch, c = this.cam;
      const k = Math.max(0.12, Math.min(2.5, (p.k0 * d) / p.d0)), wx = (p.mx - p.x0) / p.k0, wy = (p.my - p.y0) / p.k0;
      c.k = c.tk = k;
      c.x = c.tx = mx - wx * k;
      c.y = c.ty = my - wy * k;
      this.applyCam();
      return;
    }
    const d = this.drag;
    if (!d) return;
    const dx = e.clientX - d.sx, dy = e.clientY - d.sy;
    if (!d.moved) {
      if (Math.hypot(dx, dy) < 5) return;
      d.moved = true;
      if (d.type === "pan") this.view.classList.add("panning");
      else d.el.classList.add("dragging");
    }
    if (d.type === "pan") {
      this.cam.tx = this.cam.x = d.ox + dx;
      this.cam.ty = this.cam.y = d.oy + dy;
      this.applyCam();
    } else if (!d.n.fixed) {
      d.n.tx = d.n.x = d.ox + dx / this.cam.k;
      d.n.ty = d.n.y = d.oy + dy / this.cam.k;
      this.position(d.n);
      this.drawEdges();
    }
  }
  private up(e: PointerEvent) {
    this.pts.delete(e.pointerId);
    if (this.pinch) {
      if (this.pts.size < 2) this.pinch = null;
      return;
    }
    const d = this.drag;
    if (!d) return;
    this.drag = null;
    this.view.classList.remove("panning");
    if (d.type === "node") {
      d.el.classList.remove("dragging");
      if (d.moved) this.onNodeMoved(d.n);
      else this.onNodeClick(d.n.id, e);
    } else if (!d.moved) this.onBackgroundClick();
  }
  private cancel(e: PointerEvent) {
    this.pts.delete(e.pointerId);
    if (this.pts.size < 2) this.pinch = null;
    if (this.drag?.type === "node") this.drag.el.classList.remove("dragging");
    this.drag = null;
    this.view.classList.remove("panning");
  }
  private wheel(e: WheelEvent) {
    e.preventDefault();
    if (e.ctrlKey || e.metaKey) this.zoomAt(e.clientX, e.clientY, Math.exp(-e.deltaY * 0.01));
    else {
      this.cam.tx = this.cam.x = this.cam.tx - e.deltaX;
      this.cam.ty = this.cam.y = this.cam.ty - e.deltaY;
      this.applyCam();
    }
  }
}

/**
 * goal-tracker's sector layout over a tree. The root sits at the centre; each child gets an
 * angular sector proportional to its leaf count, recursively, and sits at the middle of its
 * sector on a ring per depth. Pinned nodes keep their dragged position. A short relaxation
 * pass separates overlapping cards without moving pinned ones.
 */
export function layoutTree(root: Node, radii: number[], pinned: Map<string, [number, number]>, rankOf: (id: string) => number = () => 0) {
  const memo = new Map<Node, number>();
  const leaves = (n: Node): number => {
    let v = memo.get(n);
    if (v === undefined) {
      v = n.children.length ? n.children.reduce((s, c) => s + leaves(c), 0) : 1;
      memo.set(n, v);
    }
    return v;
  };
  const place = (n: Node, depth: number, a0: number, a1: number) => {
    const pin = pinned.get(n.id);
    if (depth === 0) {
      n.tx = 0;
      n.ty = 0;
    } else {
      const a = (a0 + a1) / 2, r = radii.slice(0, depth).reduce((s, x) => s + x, 0) + (n.rowOffset ?? 0);
      n.tx = pin ? pin[0] : Math.cos(a) * r;
      n.ty = pin ? pin[1] : Math.sin(a) * r;
    }
    if (!n.children.length) return;
    if (depth === 0) {
      a0 = -Math.PI / 2;
      a1 = a0 + Math.PI * 2;
    } else {
      // a little air between neighbouring domains' fans
      const mid = (a0 + a1) / 2, w = Math.min((a1 - a0) * 0.84, depth === 1 ? Math.PI * 0.9 : Math.PI * 0.55);
      a0 = mid - w / 2;
      a1 = mid + w / 2;
    }
    const tot = leaves(n);
    let a = a0;
    const kids = [...n.children].sort((x, y) => x.id.localeCompare(y.id));
    // Children sit on rows by dependency rank: what helps others is nearest the parent,
    // what needs them further out. Within a rank, stagger when the arc cannot fit them.
    const r = radii.slice(0, depth + 1).reduce((s, x) => s + x, 0);
    const arc = (a1 - a0) * r;
    const rowGap = Math.max(...kids.map((k) => k.h), 40) + 26;
    const byRank = new Map<number, Node[]>();
    for (const k of kids) byRank.set(rankOf(k.id), [...(byRank.get(rankOf(k.id)) ?? []), k]);
    const stagger = new Map<Node, number>();
    for (const [, group] of byRank) {
      const need = group.reduce((s, c) => s + c.w + 24, 0);
      const rows = Math.max(1, Math.min(3, Math.ceil(need / Math.max(arc, 1))));
      group.forEach((k, i) => stagger.set(k, rows > 1 ? (i % rows) * 0.45 : 0));
    }
    if (byRank.size > 1) {
      // Concentric rows: each rank spreads across the whole sector on its own row.
      for (const [rk, group] of byRank) {
        group.sort((x, y) => x.id.localeCompare(y.id));
        const step = (a1 - a0) / group.length;
        group.forEach((ch, i) => {
          ch.rowOffset = (rk + (stagger.get(ch) ?? 0)) * rowGap;
          place(ch, depth + 1, a0 + i * step, a0 + (i + 1) * step);
        });
      }
      return;
    }
    for (const ch of kids) {
      const span = ((a1 - a0) * leaves(ch)) / tot;
      ch.rowOffset = (stagger.get(ch) ?? 0) * rowGap;
      place(ch, depth + 1, a, a + span);
      a += span;
    }
  };
  // Ring 1 must fit every domain card side by side; grow it with their number.
  const top = root.children;
  const need1 = top.reduce((s, c) => s + c.w + 40, 0);
  radii = [Math.max(radii[0] ?? 500, need1 / (2 * Math.PI)), ...radii.slice(1)];
  place(root, 0, 0, 0);
  const all: Node[] = [];
  const collect = (n: Node) => {
    all.push(n);
    n.children.forEach(collect);
  };
  collect(root);
  // relax only among nodes of the same depth (different depths live on different rings)
  const pad = 26;
  for (let it = 0; it < 80; it++) {
    let moved = false;
    for (let i = 0; i < all.length; i++)
      for (let j = i + 1; j < all.length; j++) {
        const a = all[i]!, b = all[j]!;
        if (a.depth !== b.depth || a.depth === 0) continue;
        const ox = (a.w + b.w) / 2 + pad - Math.abs(a.tx - b.tx), oy = (a.h + b.h) / 2 + pad - Math.abs(a.ty - b.ty);
        if (ox <= 0 || oy <= 0) continue;
        moved = true;
        const ma = pinned.has(a.id) ? 0 : 1, mb = pinned.has(b.id) ? 0 : 1, tot = ma + mb || 1;
        if (ox < oy) {
          const s = (a.tx < b.tx ? -1 : 1) * ox * 0.55;
          a.tx += (s * ma) / tot;
          b.tx -= (s * mb) / tot;
        } else {
          const s = (a.ty < b.ty ? -1 : 1) * oy * 0.55;
          a.ty += (s * ma) / tot;
          b.ty -= (s * mb) / tot;
        }
      }
    if (!moved) break;
  }
}
